import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ErrorHandlerService } from '../error-handler/error-handler.service';
import { LanguageService } from '../cache/language.service';
import { OidcAuthService } from '../oidc-auth/oidc-auth.service';
import { HttpCacheService } from '../cache/http-cache.service';
import { Injectable, inject } from '@angular/core';
import { CorrelationIdService } from '../services/correlation-id.service';

@Injectable()
/** Service to Intercept Http request or response */
export class HttpInterceptorService implements HttpInterceptor {
  errorHandler = inject(ErrorHandlerService);
  languageService = inject(LanguageService);
  private httpCacheService = inject(HttpCacheService);
  private oidcAuthService = inject(OidcAuthService);
  private correlationIdService = inject(CorrelationIdService);

  constructor() {
    this.getAcceptedLanguage();
  }

  /* #region Public Methods */
  /** Method to intercept Http request or response */
  public intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const correlationId = this.correlationIdService.getCorrelationId();
    if (request.method === "GET" && request.params.get("cch") === "1") {
      const cachedResponse: HttpResponse<any> = this.httpCacheService.get(
        request.url
      );
      if (cachedResponse) {
        return of(cachedResponse);
      }
    }
    let headers = new HttpHeaders();
    if (this.oidcAuthService.isUrlProtected(request.url)) {
      const authHeader = this.oidcAuthService.authorizationHeaderValue;
      if (authHeader) {
        if (request.params.get("blob") === "1") {
          headers = headers.set("Authorization", authHeader);
        } else {
          headers = headers
            .set("Authorization", authHeader)
            .set("Accept-Language", this.getAcceptedLanguage())
            .set("Content-Type", "application/json");
        }
      }
    } else if (!request.withCredentials) {
      headers = headers.set("Accept-Language", this.getAcceptedLanguage());
    }
    headers = headers.set('X-Correlation-ID', correlationId);
    request = request.clone({ headers });

    return next.handle(request).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          if (request.params.get("cch") === "1") {
            this.httpCacheService.put(request.url, event);
          }
        }
      }),
      catchError((error: any) => {
        if (error instanceof HttpErrorResponse) {
          if (error.status !== 401) {
            return this.errorHandler.handleError(error);
          }
          else if (error.status === 401  && 
            error.error?.detail?.indexOf('is not configured in BRiCS') !== -1 &&
            error.error?.detail?.indexOf('Roles and Permissions for the user') !== -1) 
            {
            return this.errorHandler.handleError(error);
          }
            else if(error.status === 401 && error.error?.detail?.startsWith("Invalid Access Token/Access denied to the API"))
            {
            return this.errorHandler.handleError(error);
          }
            else if (error.status === 401)
            {
            // Only trigger login if we have a protected URL and user is not logged in
            if (this.oidcAuthService.isUrlProtected(error.url || '') && !this.oidcAuthService.isLoggedIn()) {
              this.oidcAuthService.login();
            } else {
              return this.errorHandler.handleError(error);
            }
          }
        }
        return null;
      })
    );
  }
  /* #endregion */

  /* #region Private Methods */
  /** Method to get language from storage to set accept-language in headers */
  private getAcceptedLanguage(): string {
    return this.languageService.getLanguage().Name;
  }
  /* #endregion */
}
